import { useCallback, useEffect, useState } from 'react';
import SwaggerClient from 'swagger-client';
import config from '../config';
import { reportApiFailure } from '../helpers/chaosReporter';

const API_DOCS = `${config.apiUrl}/api-docs`;

let swaggerCache = null;

function getSwaggerClient() {
    if (swaggerCache === null) {
        swaggerCache = new SwaggerClient(API_DOCS);
    }
    // TODO: handle swagger cache errors here
    return Promise.resolve(swaggerCache);
}

export async function doSwaggerCall(api, operation, params) {
    const client = await getSwaggerClient();
    const authorizations = {};
    if (window.ACTIVITER_CLIENT) {
        authorizations.clientId = window.ACTIVITER_CLIENT;
    }
    const token = localStorage.getItem('userToken');
    if (token) {
        authorizations.apiKey = token;
    }
    client.authorizations = authorizations;

    try {
        const res = await client.apis[api][operation](params);
        return res.body;
    } catch (err) {
        reportApiFailure(api, operation, params, err);
        throw err;
    }
}

export function uploadImage(file) {
    return doSwaggerCall('files', 'uploadFile', { file }).then(data => data.url);
}

export function useSwaggerClient(
    api,
    operation,
    params = {},
    extraDependencies = [],
    mapping = x => x
) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [data, setData] = useState(null);
    const [internalReload, setInternalReload] = useState(0);

    const reloadData = useCallback(() => {
        setInternalReload(x => x + 1);
    }, [setInternalReload]);
    const mappingCallback = useCallback(
        x => mapping(x),
        // no dependency, this is just a way to prevent re-renders
        []
    );

    useEffect(() => {
        setLoading(true);

        // to keep the same logic as it was in useGetApiCall
        if (api === false || operation === false) {
            return undefined;
        }
        // cancellation token type of effect handling
        let stillValid = true;

        doSwaggerCall(api, operation, params)
            .then(result => {
                if (!stillValid) {
                    return;
                }
                setData(mappingCallback(result));
                setError(false);
                setLoading(false);
            })
            .catch(err => {
                console.log(err);
                if (!stillValid) {
                    return;
                }
                setData(null);
                setError(err);
                setLoading(false);
            });
        return () => {
            stillValid = false;
        };
    }, [
        api,
        operation,
        mappingCallback,
        // yep, this needs to be the same (in string) to prevent calling it all over again
        JSON.stringify(params),
        // same for extra dependencies
        JSON.stringify(extraDependencies),
        // force reload data
        internalReload
    ]);

    return [data, loading, error, setData, reloadData];
}
